
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import clsx from 'clsx'
import fs from 'fs'
import {MDXRemote, MDXRemoteSerializeResult} from 'next-mdx-remote'
import {serialize} from 'next-mdx-remote/serialize'
import useTranslation from 'next-translate/useTranslation'
import {useRouter} from 'next/router'
import {join} from 'path'
import {HTMLProps} from 'react'
import Button from '../../components/atoms/button'
import Container from '../../components/atoms/container'
import Heading, {HeadingVariants} from '../../components/atoms/heading'
import BackIcon from '../../components/atoms/icons/back-icon'
import Paragraph from '../../components/atoms/paragraph'
import {
  FooterDataStateProps,
  FooterProvider,
} from '../../contexts/footer-context'
import PageLayout from '../../layouts/page-layout'
import fetchGeneralSettings from '../../lib/fetch-general-settings'
import fetchSocialLinks from '../../lib/fetch-social-links'
import bishopSymbol from '../../public/assets/bishop-logo-symbol.png'
import {JobOpening as JobOpeningDataProps} from '../../types'

export interface JobOpeningDetailsProps<T = string>
  extends FooterDataStateProps {
  data?: JobOpeningDataProps<T>
}

function CustomList({className, children}: HTMLProps<HTMLUListElement>) {
  return (
    <ul
      className={clsx('list-disc list-inside pl-4 text-typography', className)}
    >
      {children}
    </ul>
  )
}

function CustomHeading(variant: HeadingVariants) {
  return function HeadingComponent({children}: HTMLProps<HTMLHeadingElement>) {
    return (
      <Heading disableMaxWidth component="h2" variant={variant}>
        {children}
      </Heading>
    )
  }
}

function CustomLink({children, ...props}: HTMLProps<HTMLAnchorElement>) {
  return (
    <a className="text-primary hover:underline" {...props}>
      {children}
    </a>
  )
}

export default function JobOpeningDetails({
  data,
  social,
  general,
}: JobOpeningDetailsProps<MDXRemoteSerializeResult>) {
  const {t} = useTranslation('careers')
  const router = useRouter()

  if (router.isFallback || !data) {
    return <></>
  }

  return (
    <FooterProvider value={{social, general}}>
      <PageLayout
        disableDefaultPadding
        className="pt-28 md:pt-32 pb-12 md:pb-24"
        title={data.title}
        description={data.shortDescription}
        openGraph={{
          images: [{url: data.previewImage || bishopSymbol.src}],
          url: `careers/${data.pathname}`,
        }}
        prefixImageUrl={!data.previewImage}
      >
        <Container component="article" className="grid gap-4 text-lg list-disc">
          <Button
            renderAsLink
            href="/careers"
            className="place-self-start grid grid-flow-col gap-2 items-center place-content-start"
            variant="secondary"
          >
            <BackIcon />

            {t('general.back')}
          </Button>

          <Heading variant="h1" disableMaxWidth>
            {data.title}
          </Heading>

          <MDXRemote
            {...data.description}
            components={{
              h1: CustomHeading('h2'),
              h2: CustomHeading('h2'),
              h3: CustomHeading('h3'),
              h4: CustomHeading('h4'),
              h5: CustomHeading('h5'),
              h6: CustomHeading('h6'),
              ul: CustomList,
              p: Paragraph,
              a: CustomLink,
            }}
          />

          <Button
            renderAsLink
            href={`/contact-us/apply?job=${encodeURIComponent(data.title)}`}
            className="place-self-start"
            variant="primary"
          >
            {t('general.apply-now')}
          </Button>
        </Container>
      </PageLayout>
    </FooterProvider>
  )
}

 async function _getStaticProps({...ctx}) {
  try {
    const {slug} = ctx.params
    const parseFrontMatter = await import('../../lib/parse-front-matter')
    const content = await import(`../../content/job-openings/${slug}.md`)
    const {attributes: data} = parseFrontMatter.default<any>(content.default)
    const description = await serialize(data.description)

    return {
      props: {
        data: {
          title: data.title,
          shortDescription: data['short-description'],
          previewImage: data['preview-image'] || null,
          pathname: slug,
          description,
          categories: data.categories,
        },
        social: fetchSocialLinks(),
        general: fetchGeneralSettings(),
      },
    }
  } catch {
    return {
      notFound: true,
    }
  }
}

export async function getStaticPaths() {
  const JOB_LISTINGS = join(process.cwd(), 'content/job-openings')
  const paths = fs
    .readdirSync(JOB_LISTINGS)
    .map(path => path.replace(/\.mdx?$/, ''))
    .map(slug => ({params: {slug}}))

  return {
    paths,
    fallback: true,
  }
}


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/careers/[slug]',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  